// Sprint fc-conversation-panel — the per-conversation right panel (A7,
// target-state-cases.md). Renders inside `ChatView`'s subtree only (§4-D10:
// "in ChatView and in no other file" — the collapse toggle lives HERE, so
// `ChatHeader.tsx` is never opened).
//
// Four sections, top→bottom = A7's own order (spec §2, LOCKED):
//   S1 Campaña, sprint y fase — empty state, owner `aos-harness-runtime`
//   S2 Documentos y enlaces   — real data
//   S3 Progreso               — real data
//   S4 Resumen                — empty state, owner `aos-observability`
//
// Zero fetch, zero socket subscription, zero polling `useEffect` (§4
// SSoT audit item 6) — every byte rendered is already in the store
// because `ChatView` fetched it.

import { useMemo } from 'react';
import type { Message } from '../../../types';
import { useTranslation } from '../../../i18n';
import { useAppStore } from '../../../stores/app.store';
import { useDocViewer } from '../../../hooks/useDocViewer';
import { deriveProgress } from '../../../lib/tool-rendering/derive-progress';
import { Icon } from '../../atoms/Icon';
import { DocPathChip } from '../../DocPathChip';
import { usePanelCollapse } from './usePanelCollapse';
import { collectDocLinks } from './collect-doc-links';

export interface ConversationPanelProps {
  agentId: string;
}

// Stable reference for the "no messages loaded yet" case — a fresh `[]`
// literal INSIDE a zustand selector is a new value on every call, which
// breaks `useSyncExternalStore`'s snapshot equality check and drives an
// infinite render loop. One shared empty array, never mutated.
const EMPTY_MESSAGES: Message[] = [];

export function ConversationPanel({ agentId }: ConversationPanelProps) {
  const { t } = useTranslation();
  const { collapsed, toggle } = usePanelCollapse(agentId);

  if (collapsed) {
    return (
      <div
        className="w-10 shrink-0 border-l border-border flex flex-col items-center pt-2"
        data-testid="conversation-panel"
        data-panel-collapsed="true"
      >
        <button
          type="button"
          onClick={toggle}
          aria-label={t('conversationPanel.expand')}
          data-testid="conversation-panel-toggle"
          className="p-1 rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-motion-instant"
        >
          <Icon name="ChevronLeft" size={16} />
        </button>
      </div>
    );
  }

  return (
    <div
      className="w-80 shrink-0 border-l border-border flex flex-col min-h-0"
      data-testid="conversation-panel"
      data-panel-collapsed="false"
    >
      <div className="shrink-0 flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-caption font-medium text-muted-foreground">
          {t('conversationPanel.title')}
        </span>
        <button
          type="button"
          onClick={toggle}
          aria-label={t('conversationPanel.collapse')}
          data-testid="conversation-panel-toggle"
          className="p-1 rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-motion-instant"
        >
          <Icon name="ChevronRight" size={16} />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4">
        <CampaignSection />
        <DocumentsSection agentId={agentId} />
        <ProgressSection agentId={agentId} />
        <SummarySection />
      </div>
    </div>
  );
}

// ── S1 — Campaña, sprint y fase · empty state, owner aos-harness-runtime ──

function CampaignSection() {
  const { t } = useTranslation();
  return (
    <section data-testid="conversation-panel-section-campaign">
      <h3 className="text-caption font-medium text-foreground mb-1">
        {t('conversationPanel.campaign.title')}
      </h3>
      <p className="text-body-chat text-muted-foreground">
        {t('conversationPanel.campaign.body')}
      </p>
    </section>
  );
}

// ── S2 — Documentos y enlaces · real data ──────────────────────────────

function DocumentsSection({ agentId }: { agentId: string }) {
  const { t } = useTranslation();
  const messagesRaw = useAppStore((s) => s.messagesByAgent[agentId]);
  const hasMore = useAppStore(
    (s) => s.historyByAgent[agentId]?.hasMore ?? false,
  );
  const { openDoc } = useDocViewer();

  // `messagesRaw === undefined` means `loadMessages` hasn't resolved yet
  // (A8's undefined case) — render the section's ABSENCE state, never a
  // complete-looking empty list.
  const loaded = messagesRaw !== undefined;

  const { docs, docsTotal, links, linksTotal } = useMemo(
    () => collectDocLinks(loaded ? (messagesRaw ?? []) : []),
    [loaded, messagesRaw],
  );

  return (
    <section data-testid="conversation-panel-section-documents">
      <h3 className="text-caption font-medium text-foreground mb-1">
        {t('conversationPanel.documents.title')}
      </h3>
      {!loaded ? (
        <p
          className="text-body-chat text-muted-foreground"
          data-testid="conversation-panel-documents-loading"
        >
          {t('conversationPanel.documents.loading')}
        </p>
      ) : (
        <>
          {hasMore && (
            <p
              className="text-caption text-muted-foreground/70 mb-2"
              data-testid="conversation-panel-documents-partial-notice"
            >
              {t('conversationPanel.documents.partialWindow')}
            </p>
          )}

          <h4 className="text-caption text-muted-foreground mb-1">
            {t('conversationPanel.documents.docsLabel')}
          </h4>
          {docs.length === 0 ? (
            <p
              className="text-body-chat text-muted-foreground/70 mb-3"
              data-testid="conversation-panel-documents-empty"
            >
              {t('conversationPanel.documents.noneYet')}
            </p>
          ) : (
            <div
              className="flex flex-wrap gap-1 mb-3"
              data-testid="conversation-panel-docs-list"
            >
              {docs.map((path) => (
                <DocPathChip key={path} path={path} onOpen={openDoc} />
              ))}
              {docsTotal > docs.length && (
                <span
                  className="text-caption text-muted-foreground/60 self-center"
                  data-testid="conversation-panel-docs-more"
                >
                  {t('conversationPanel.documents.more', {
                    count: docsTotal - docs.length,
                  })}
                </span>
              )}
            </div>
          )}

          <h4 className="text-caption text-muted-foreground mb-1">
            {t('conversationPanel.documents.linksLabel')}
          </h4>
          {links.length === 0 ? (
            <p
              className="text-body-chat text-muted-foreground/70"
              data-testid="conversation-panel-links-empty"
            >
              {t('conversationPanel.documents.noneYet')}
            </p>
          ) : (
            <ul
              className="space-y-1"
              data-testid="conversation-panel-links-list"
            >
              {links.map((url) => (
                <li key={url}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-body-chat text-action hover:text-action/80 underline underline-offset-2 break-all"
                  >
                    {url}
                  </a>
                </li>
              ))}
              {linksTotal > links.length && (
                <li
                  className="text-caption text-muted-foreground/60"
                  data-testid="conversation-panel-links-more"
                >
                  {t('conversationPanel.documents.more', {
                    count: linksTotal - links.length,
                  })}
                </li>
              )}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

// ── S3 — Progreso · real data ──────────────────────────────────────────

function ProgressSection({ agentId }: { agentId: string }) {
  const { t } = useTranslation();
  const messages = useAppStore((s) => s.messagesByAgent[agentId] ?? EMPTY_MESSAGES);
  const progress = useMemo(() => deriveProgress(messages), [messages]);

  return (
    <section data-testid="conversation-panel-section-progress">
      <h3 className="text-caption font-medium text-foreground mb-1">
        {t('conversationPanel.progress.title')}
      </h3>
      {progress === null ? (
        <p
          className="text-body-chat text-muted-foreground"
          data-testid="conversation-panel-progress-empty"
        >
          {t('conversationPanel.progress.noPlanYet')}
        </p>
      ) : (
        <div data-testid="conversation-panel-progress-readout">
          <p className="text-body-chat text-foreground">
            {t('conversationPanel.progress.readout', {
              current: progress.current,
              total: progress.total,
            })}
          </p>
          {progress.inProgressItem && (
            <p
              className="text-caption text-muted-foreground mt-0.5"
              data-testid="conversation-panel-progress-current-item"
            >
              {progress.inProgressItem}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

// ── S4 — Resumen · empty state, owner aos-observability ────────────────

function SummarySection() {
  const { t } = useTranslation();
  return (
    <section data-testid="conversation-panel-section-summary">
      <h3 className="text-caption font-medium text-foreground mb-1">
        {t('conversationPanel.summary.title')}
      </h3>
      <p className="text-body-chat text-muted-foreground">
        {t('conversationPanel.summary.body')}
      </p>
    </section>
  );
}
